import { HTMLAttributes, useCallback, useMemo, useRef, useState } from 'react'
import { useKey } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Observer } from 'gsap/dist/Observer'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import GSAPSplitText from 'gsap/dist/SplitText'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import { splitLines, type SplitText, SplitTextEngine } from '@/utils/split-text'

import { cn } from '@/lib/utils'

type SustainabilitySliderProps = HTMLAttributes<HTMLDivElement> & {
  items?: {
    title: string
    text: string
  }[]
}

gsap.registerPlugin(GSAPSplitText, Observer, ScrollTrigger)

const formatCounter = (index: number) => String(index + 1).padStart(2, '0')

const SustainabilitySlider = ({
  items,
  className,
  ...props
}: SustainabilitySliderProps) => {
  const safeItems = useMemo(() => items || [], [items])

  const [active, setActive] = useState(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const motionRef = useRef<HTMLDivElement>(null)
  const isAnimatingRef = useRef(false)
  const activeRef = useRef(0)

  // Refs for each item's DOM nodes
  const counterRefs = useRef<(HTMLSpanElement | null)[]>([])
  const titleRefs = useRef<(HTMLDivElement | null)[]>([])
  const textRefs = useRef<(HTMLDivElement | null)[]>([])

  // Refs for split instances per item
  const titleSplitsRef = useRef<(SplitText | null)[]>([])
  const textSplitsRef = useRef<(SplitText | null)[]>([])

  // Refs for counter split chars per item
  const counterCharsRef = useRef<
    { inners: HTMLElement[]; revert: () => void }[]
  >([])

  // Wrapper refs for height animation
  const titleWrapperRef = useRef<HTMLDivElement>(null)
  const textWrapperRef = useRef<HTMLDivElement>(null)

  /**
   * Returns the inner <span> of a Typography component so that
   * SplitText can work on a node whose width is constrained by the
   * block-level parent <div>.
   */
  const getTypographyInner = (wrapper: HTMLElement): HTMLElement | null => {
    const typography = wrapper.querySelector('[data-typography]')
    if (!typography) return null
    return (typography.querySelector(':scope > span') as HTMLElement) || null
  }

  const splitCounterChars = (target: HTMLElement) => {
    const split = new GSAPSplitText(target, {
      type: 'chars',
      charsClass: 'gsap-counter-char'
    })

    const inners = split.chars.map((char) => {
      const wrapper = document.createElement('span')
      wrapper.style.display = 'inline-block'
      wrapper.style.overflow = 'hidden'

      const inner = document.createElement('span')
      inner.style.display = 'inline-block'
      inner.textContent = char.textContent || ''

      char.replaceWith(wrapper)
      wrapper.appendChild(inner)

      return inner
    })

    return {
      inners,
      revert: () => split.revert()
    }
  }

  // FadeIn reveal on scroll
  useGSAP(
    () => {
      if (!motionRef.current) return

      gsap.fromTo(
        motionRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: motionRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: motionRef,
      dependencies: [safeItems],
      revertOnUpdate: true
    }
  )

  // Mount: split all items' text into lines, split counters into chars,
  // position non-active items off-screen
  useGSAP(
    () => {
      if (safeItems.length === 0) return

      // Split title and text for every item
      safeItems.forEach((_, i) => {
        const titleNode = titleRefs.current[i]
        const textNode = textRefs.current[i]

        if (titleNode) {
          const inner = getTypographyInner(titleNode)
          if (inner) {
            inner.style.display = 'block'
            titleSplitsRef.current[i] = splitLines(inner, SplitTextEngine.GSAP)
          }
        }

        if (textNode) {
          const inner = getTypographyInner(textNode)
          if (inner) {
            inner.style.display = 'block'
            textSplitsRef.current[i] = splitLines(inner, SplitTextEngine.GSAP)
          }
        }

        // Split counter chars
        const counterNode = counterRefs.current[i]
        if (counterNode) {
          counterCharsRef.current[i] = splitCounterChars(counterNode)
        }
      })

      // Set non-active items' lines to yPercent 110 (hidden below)
      safeItems.forEach((_, i) => {
        if (i === activeRef.current) return

        const titleLines = titleSplitsRef.current[i]?.lines || []
        const textLines = textSplitsRef.current[i]?.lines || []
        gsap.set([...titleLines, ...textLines], { yPercent: 110 })

        // Hide counter chars too
        const counterChars = counterCharsRef.current[i]
        if (counterChars) {
          gsap.set(counterChars.inners, { yPercent: 110 })
        }
      })
    },
    {
      scope: rootRef,
      dependencies: []
    }
  )

  const goPrev = useCallback(() => {
    if (safeItems.length === 0 || isAnimatingRef.current) return
    setActive((prev) => (prev - 1 + safeItems.length) % safeItems.length)
  }, [safeItems])

  const goNext = useCallback(() => {
    if (safeItems.length === 0 || isAnimatingRef.current) return
    setActive((prev) => (prev + 1) % safeItems.length)
  }, [safeItems])

  // Transition animation
  useGSAP(
    () => {
      if (safeItems.length === 0) return
      if (active === activeRef.current) return

      isAnimatingRef.current = true

      const prevIndex = activeRef.current
      const nextIndex = active

      // --- Gather lines ---
      const outTitleLines = titleSplitsRef.current[prevIndex]?.lines || []
      const outTextLines = textSplitsRef.current[prevIndex]?.lines || []
      const outLines = [...outTitleLines, ...outTextLines]

      const inTitleLines = titleSplitsRef.current[nextIndex]?.lines || []
      const inTextLines = textSplitsRef.current[nextIndex]?.lines || []
      const inLines = [...inTitleLines, ...inTextLines]

      // --- Gather counter chars ---
      const outCounterChars = counterCharsRef.current[prevIndex]?.inners || []
      const inCounterChars = counterCharsRef.current[nextIndex]?.inners || []

      // --- Measure heights ---
      const titleWrapper = titleWrapperRef.current
      const textWrapper = textWrapperRef.current

      // Current heights
      const titleCurrentH = titleWrapper?.getBoundingClientRect().height || 0
      const textCurrentH = textWrapper?.getBoundingClientRect().height || 0

      // Temporarily make next item relative to measure its natural height
      const prevTitleNode = titleRefs.current[prevIndex]
      const nextTitleNode = titleRefs.current[nextIndex]
      const prevTextNode = textRefs.current[prevIndex]
      const nextTextNode = textRefs.current[nextIndex]

      if (prevTitleNode) prevTitleNode.style.position = 'absolute'
      if (nextTitleNode) nextTitleNode.style.position = 'relative'
      if (prevTextNode) prevTextNode.style.position = 'absolute'
      if (nextTextNode) nextTextNode.style.position = 'relative'

      const titleNextH = titleWrapper?.getBoundingClientRect().height || 0
      const textNextH = textWrapper?.getBoundingClientRect().height || 0

      // Lock both to current height, put items back
      if (titleWrapper)
        gsap.set(titleWrapper, { height: titleCurrentH, overflow: 'hidden' })
      if (textWrapper)
        gsap.set(textWrapper, { height: textCurrentH, overflow: 'hidden' })

      // Keep both items absolute during transition
      if (nextTitleNode) nextTitleNode.style.position = 'absolute'
      if (prevTitleNode) prevTitleNode.style.position = 'absolute'
      if (nextTextNode) nextTextNode.style.position = 'absolute'
      if (prevTextNode) prevTextNode.style.position = 'absolute'

      // Ensure incoming lines start at 110%
      gsap.set(inLines, { yPercent: 110 })
      gsap.set(inCounterChars, { yPercent: 110 })

      const tl = gsap.timeline({
        onComplete: () => {
          // Set the next item to relative (defines height), prev stays absolute
          if (nextTitleNode) nextTitleNode.style.position = 'relative'
          if (nextTextNode) nextTextNode.style.position = 'relative'

          // Release wrapper heights
          if (titleWrapper)
            gsap.set(titleWrapper, { height: 'auto', overflow: 'visible' })
          if (textWrapper)
            gsap.set(textWrapper, { height: 'auto', overflow: 'visible' })

          activeRef.current = nextIndex
          isAnimatingRef.current = false
        }
      })

      // Phase 1: Animate out — current lines y 0% → 110%, counter chars y 0% → 110%
      tl.to(outLines, {
        yPercent: 110,
        duration: 0.4,
        stagger: 0.04,
        ease: 'power2.in'
      })

      tl.to(
        outCounterChars,
        {
          yPercent: 110,
          duration: 0.4,
          stagger: 0.04,
          ease: 'power2.in'
        },
        '<'
      )

      // Phase 2: Animate in — next lines y 110% → 0%, counter chars y 110% → 0%
      // + wrapper heights transition
      tl.to(inLines, {
        yPercent: 0,
        duration: 0.45,
        stagger: 0.04,
        ease: 'power2.out'
      })

      tl.to(
        inCounterChars,
        {
          yPercent: 0,
          duration: 0.45,
          stagger: 0.04,
          ease: 'power2.out'
        },
        '<'
      )

      if (titleWrapper) {
        tl.to(
          titleWrapper,
          {
            height: titleNextH,
            duration: 0.45,
            ease: 'power2.inOut'
          },
          '<'
        )
      }

      if (textWrapper) {
        tl.to(
          textWrapper,
          {
            height: textNextH,
            duration: 0.45,
            ease: 'power2.inOut'
          },
          '<'
        )
      }
    },
    {
      scope: rootRef,
      dependencies: [active],
      revertOnUpdate: false
    }
  )

  useKey('ArrowLeft', goPrev)
  useKey('ArrowRight', goNext)

  // Swipe left/right via GSAP Observer (touch + pointer)
  const goPrevRef = useRef(goPrev)
  goPrevRef.current = goPrev
  const goNextRef = useRef(goNext)
  goNextRef.current = goNext

  useGSAP(
    () => {
      if (!rootRef.current || safeItems.length <= 1) return

      const obs = Observer.create({
        target: rootRef.current,
        type: 'touch,pointer',
        onLeft: () => goNextRef.current(),
        onRight: () => goPrevRef.current(),
        tolerance: 50,
        preventDefault: true
      })

      return () => obs.kill()
    },
    { scope: rootRef, dependencies: [safeItems.length] }
  )

  return (
    <Section
      className={cn('max-w-full overflow-hidden', className)}
      {...props}
      ref={rootRef}
    >
      <div ref={motionRef} data-motion-gallery className="opacity-0">
        <Spacer variant="xl" />
        <Container>
        <Grid>
          <div
            className="col-span-2 md:col-span-6 lg:col-span-5"
            data-motion-counter
          >
            <Typography variant="huge" className="inline-flex min-w-[2ch]">
              <span className="relative inline-block">
                {safeItems.map((_, i) => (
                  <span
                    key={i}
                    ref={(el) => {
                      counterRefs.current[i] = el
                    }}
                    className={cn(i === 0 ? 'relative' : 'absolute inset-0')}
                  >
                    {formatCounter(i)}
                  </span>
                ))}
              </span>
            </Typography>
          </div>
          <div className="relative col-span-2 md:col-span-6 lg:col-span-7">
            <div className="flex flex-col gap-spacing-lg" data-motion-wrapper>
              <Spacer variant="xl" />

              <div className="relative" ref={titleWrapperRef}>
                {safeItems.map((item, i) => (
                  <div
                    key={i}
                    ref={(el) => {
                      titleRefs.current[i] = el
                    }}
                    className={cn(
                      'left-0 top-0 w-full',
                      i === 0 ? 'relative' : 'absolute'
                    )}
                  >
                    <Typography variant="h3" data-motion-title>
                      {item.title}
                    </Typography>
                  </div>
                ))}
              </div>

              <div className="relative" ref={textWrapperRef}>
                {safeItems.map((item, i) => (
                  <div
                    key={i}
                    ref={(el) => {
                      textRefs.current[i] = el
                    }}
                    className={cn(
                      'left-0 top-0 w-full',
                      i === 0 ? 'relative' : 'absolute'
                    )}
                  >
                    <Typography
                      variant="h6"
                      data-motion-text
                      className="text-text-secondary-light"
                    >
                      {item.text}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>

            <Spacer variant="lg" />
            <div className="flex gap-spacing-2xs">
              <button
                type="button"
                aria-label="Slide precedente"
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-[rgba(0,0,0,0.2)]"
                onClick={goPrev}
              >
                <SVGIcon
                  symbol="arrow-prev"
                  className="size-[16px]"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                aria-label="Slide successiva"
                className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-[rgba(0,0,0,0.2)]"
                onClick={goNext}
              >
                <SVGIcon
                  symbol="arrow-next"
                  className="size-[16px]"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </Grid>
      </Container>
      <Spacer variant="xl" />
      </div>
    </Section>
  )
}

export default SustainabilitySlider
